<template>
  <content-dialog
    v-model="dialogOpen"
    no-backdrop-dismiss
    :position="$q.screen.lt.md ? 'bottom' : 'top'"
    :full-width="$q.screen.lt.md"
    :transition-show="$q.screen.lt.md ? 'slide-up' : 'slide-down'"
    :transition-hide="$q.screen.lt.md ? 'slide-down' : 'slide-up'"
    :maximized="$q.screen.lt.md"
    :persistent="!$q.screen.lt.md"
    width="400px"
    title="Take Payment"
    tags="manual payment"
    :loading="loading"
    @hide="onHide"
  >
    <div
      v-if="hasPaymentProvider && invoice.total > 0 && (!!invoice.full_payment_url || showPaymentProviderBtn)"
      class="q-mb-md"
    >
      <sticky-header>
        Card Payment
      </sticky-header>
      <q-card-section v-if="!!invoice.full_payment_url" class="q-pt-none">
        <q-item-label
          caption
          class="q-mb-sm"
        >
          The following URL is a direct link to pay for this invoice by card
        </q-item-label>

        <q-input
          :model-value="invoice.full_payment_url"
          autogrow
          readonly
          outlined
          dense
          hide-bottom-space
        />
      </q-card-section>

      <q-toolbar v-if="showPaymentProviderBtn" class="q-px-md">
        <SquareAppBtn
          :location-id="options.location_id || null"
          :invoice="invoice"
          @success="onExternalPaymentSuccess"
        />
      </q-toolbar>
    </div>

    <div>
      <sticky-header>
        Manual Payment
      </sticky-header>
      <q-card-section v-if="!!payment" class="q-pt-none">
        <q-item-label
          caption
          class="q-mb-md"
        >
          Use manual payments to record payments received via cash or a third-party system.
        </q-item-label>
        <div class="row q-col-gutter-md wrap q-mb-md">
          <div class="col-12 col-sm-6">
            <q-input
              v-model="payment.amount"
              label="Amount"
              type="number"
              step="0.01"
              min="0.01"
              outlined
              prefix="$"
              :error="!!errors['amount']"
              :error-message="errors['amount'] ? errors['amount'][0] : null"
              class="required-astrx-container"
              hide-bottom-space
            />
          </div>
          <div class="col-12 col-sm-6">
            <date-picker
              v-model="payment.date"
              label="Date"
              required
              outlined
              :error="!!errors['date']"
              :error-message="errors['date'] ? errors['date'][0] : null"
            />
          </div>
        </div>
        <q-input
          v-model="payment.description"
          stack-label
          label="Description"
          rows="2"
          type="textarea"
          outlined
          hide-bottom-space
          class="q-my-md"
          :error="!!errors['description']"
          :error-message="errors['description'] ? errors['description'][0] : null"
        />
        <q-select
          v-model="payment.paid_via"
          :options="paymentViaOptions"
          stack-label
          label="Paid Via"
          outlined
          hide-bottom-space
          class="q-my-md required-astrx-container"
          :error="!!errors['paid_via']"
          :error-message="errors['paid_via'] ? errors['paid_via'][0] : null"
        />
        <q-input
          v-model="payment.transaction_id"
          stack-label
          label="Transaction Number"
          outlined
          hide-bottom-space
          :error="!!errors['transaction_id']"
          :error-message="errors['transaction_id'] ? errors['transaction_id'][0] : null"
          hint="Optional transaction number for payments accepted via card readers or third party software."
        />
      </q-card-section>
    </div>

    <template #bottom>
      <q-toolbar v-if="!!payment">
        <q-space />
        <q-btn
          color="green"
          label="Save"
          icon-right="done"
          @click="$refs.emailDialog.open()"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <SendEmailDialog
    ref="emailDialog"
    color="green"
    requires-client
    action-name="create payment"
    title="Send receipt to client?"
    :email-order="['BILLING', 'PRIMARY', 'EXTRA']"
    :saved-email="invoice.last_sent_to_email"
    :client="invoice.care_of_client || invoice.client"
    :loading="loading"
    :error="!!errors['last_sent_to_email']"
    @complete="submitPayment"
  >
    <div
      v-if="!!invoice.client"
    >
      Do you want to send {{ invoice.client.display_name }} a receipt of this invoice?
    </div>
  </SendEmailDialog>
</template>

<script>
import SendEmailDialog from 'components/partials/communications/email/SendEmailDialog'
import paymentMixin from 'components/app/payments/partials/mixins/paymentMixin'
import SquareAppBtn from 'components/partials/payments/hardware/SquareAppBtn.vue'

export default {
  components: {
    SquareAppBtn,
    SendEmailDialog
  },
  mixins: [paymentMixin],
  props: {
    invoice: {
      type: Object,
      required: true
    },
    amount: {
      type: [Number, String],
      default: null
    }
  },
  emits: ['saved'],
  data () {
    return {
      errors: {},
      dialogOpen: false,
      payment: null,
      loading: false,
      manualPayment: false,
      options: null
    }
  },
  computed: {
    hasPaymentProvider () {
      return this.$store.getters['providers/hasLinkedPaymentProvider']
    },
    paymentProvider () {
      return this.$store.getters['providers/getPaymentProvider']
    },
    showPaymentProviderBtn () {
      return this.$q.platform.is.capacitor && this.options && this.paymentProvider && this.paymentProvider.driver === 'square'
    }
  },
  methods: {
    open () {
      this.dialogOpen = true
      this.errors = {}

      // amount_paid is not always returned by GET /v1/invoice, so derive it from the
      // recorded payments too and trust whichever is larger.
      const payments = Array.isArray(this.invoice.payments) ? this.invoice.payments : []
      const paymentsSum = payments.reduce((sum, p) => sum + (this.sanitiseAmount(p?.amount) || 0), 0)
      const amountPaid = Math.max(this.sanitiseAmount(this.invoice.amount_paid) || 0, paymentsSum)
      const invoiceTotal = this.sanitiseAmount(this.invoice.total)
      const amountDueFallback = invoiceTotal !== null ? invoiceTotal - amountPaid : null

      // Default to the outstanding balance: prefer the amount passed from the parent,
      // then the locally derived remaining balance, then the backend amount_due.
      const amountDue = this.sanitiseAmount(
        this.amount ??
        amountDueFallback ??
        this.invoice.amount_due
      ) ?? 0

      this.payment = {
        invoice_id: this.invoice.id,
        amount: amountDue,
        date: this.luxon.now().toSQLDate(),
        description: null,
        paid_via: this.paymentViaOptions[0],
        transaction_id: null
      }
      this.loadPaymentDefaults()
    },
    onHide () {
      this.payment = null
      this.manualPayment = false
    },
    async submitPayment (emailPayload, done) {
      this.errors = {}
      this.loading = true
      const preparedPayment = {
        invoice_id: this.payment.invoice_id || this.invoice.id,
        amount: this.sanitiseAmount(this.payment.amount) ?? 0,
        date: this.payment.date,
        description: this.payment.description ?? null,
        transaction_id: this.payment.transaction_id ?? null,
        paid_via: this.payment.paid_via || this.paymentViaOptions[0],
        send_email: !!emailPayload.sendEmail,
        last_sent_to_email: emailPayload.recipient ?? null
      }
      const { status, payload, errors } = await this.$post('v1/payments/manual',
        preparedPayment
      )
      this.loading = false
      if (status === 'success') {
        this.dialogOpen = false
        this.$q.notify({
          color: 'green',
          message: 'Manual payment saved successfully.'
        })
        this.$bus.emit('invoices:reload')
        this.$emit('saved', payload.payment, payload.fully_paid)
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }

      if (!errors['last_sent_to_email']) {
        done()
      }
    },
    async loadPaymentDefaults () {
      const { status, payload } = await this.$get('v1/payments/defaults')
      this.loading = false
      if (status === 'success') {
        this.options = payload
      }
    },
    onExternalPaymentSuccess (payload) {
      this.payment.transaction_id = payload.transaction_id
      this.payment.description = payload.description
      this.payment.paid_via = payload.paid_via
      this.$refs.emailDialog.open()
    },
    sanitiseAmount (value) {
      const parsed = Number(value)
      if (isNaN(parsed)) {
        return null
      }
      return Math.max(parsed, 0)
    }
  }
}
</script>
